/*
Remarquables catalogue — full list of every remarquable tree in Paris,
with the user's discovery state. Mirrors Android RemarquablesScreen.kt
at the structural level.

Layout:
- Header with X/183 captured counter.
- List of cards, one per remarquable, sorted by qualification then
  species. Each card shows species name (from arbresFeatures + speciesIndex),
  the qualification badge, the first line of the city's résumé, and a
  capture indicator (filled glyph if discovered).
- Click on a card → REMARQUABLE_DETAIL.
*/

import { navigate, kPaths } from '../router'
import { ensureSpeciesIndex, speciesNameFor, type SpeciesName } from '../data/speciesIndex'
import { ensureArbresFeatures, type ArbreFeature } from '../data/arbresFeatures'
import { ensureRemarquablesInfo, type RemarquableEntry } from '../data/remarquablesInfo'
import { listCaptures } from '../data/captureRepository'
import { getSeason } from '../data/seasonStore'
import { renderSeasonSelector, wireSeasonSelector, renderArchiveBanner } from './components/SeasonSelector'
import { renderEmptyState } from './components/EmptyState'
import { renderSkeletonBlock, renderSkeletonListCard } from './components/Skeleton'

interface CardRow
{
	arbreId: number
	speciesIndex: number
	speciesName: SpeciesName | null
	info: RemarquableEntry
	captured: boolean
}

let fActiveUnsubscribe: (() => void) | null = null  // SeasonStore subscription, released on remount

/*
Mount the catalogue screen. Renders a static shell synchronously, then
hydrates async — same pattern as the other list screens.
*/
export function mountRemarquablesScreen(inRoot: HTMLElement): void
{
	if (fActiveUnsubscribe) { fActiveUnsubscribe(); fActiveUnsubscribe = null }

	inRoot.innerHTML = `
		<div class="screen arboretum">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Remarquables</h1>
			</header>
			<div class="arboretum-toolbar">${renderSeasonSelector()}</div>
			<div class="arboretum-banner" id="rmq-banner"></div>
			<div class="arboretum-subheader" id="rmq-subheader">
				${renderSkeletonBlock({ width: '180px', height: '14px' })}
			</div>
			<div class="arboretum-list" id="rmq-list" role="list">
				${renderSkeletonListCard()}
				${renderSkeletonListCard()}
				${renderSkeletonListCard()}
				${renderSkeletonListCard()}
			</div>
		</div>
	`
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => navigate(kPaths.map()))
	// First hydrate is triggered by wireSeasonSelector emitting the current season.
	fActiveUnsubscribe = wireSeasonSelector(inRoot, () => { void hydrate(inRoot) })
}

async function hydrate(inRoot: HTMLElement): Promise<void>
{
	const vSubheader = inRoot.querySelector<HTMLElement>('#rmq-subheader')
	const vList = inRoot.querySelector<HTMLElement>('#rmq-list')
	const vBanner = inRoot.querySelector<HTMLElement>('#rmq-banner')
	if (!vSubheader || !vList) return

	const vSeason = getSeason()  // active season at hydrate time
	if (vBanner) vBanner.innerHTML = renderArchiveBanner(vSeason) ?? ''

	try
	{
		const [vFeatures, vInfo, , vAllCaptures] = await Promise.all([
			ensureArbresFeatures(),
			ensureRemarquablesInfo(),
			ensureSpeciesIndex(),
			listCaptures(),
		])
		const vCaptured = new Set<number>(
			vAllCaptures.filter((inC) => inC.season === vSeason).map((inC) => inC.arbreId),
		)  // arbreIds captured during the active season — drives the discovery glyph

		const vRemarquableFeatures = vFeatures.filter((inF) => inF.remarquable)  // 183 entries from the geojson
		const vById = new Map<number, ArbreFeature>()  // arbreId -> feature, for fast lookup during the join
		for (const vF of vRemarquableFeatures) vById.set(vF.id, vF)

		const vRows: CardRow[] = []
		for (const vEntry of vInfo.values())
		{
			const vFeature = vById.get(vEntry.id)  // may be missing if remarquables-info has stale ids
			if (!vFeature) continue
			vRows.push({
				arbreId: vEntry.id,
				speciesIndex: vFeature.speciesIndex,
				speciesName: speciesNameFor(vFeature.speciesIndex),
				info: vEntry,
				captured: vCaptured.has(vEntry.id),
			})
		}
		vRows.sort((inA, inB) => sortKey(inA).localeCompare(sortKey(inB), 'fr'))

		const vCapturedCount = vRows.filter((inR) => inR.captured).length
		vSubheader.innerHTML = `
			<span class="arboretum-counter">
				<strong>${vCapturedCount}</strong> / ${vRows.length} remarquables
			</span>
		`

		// "No captured" hero — invitational illustration above the
		// catalogue list when the user hasn't started yet. The list
		// itself stays visible below so the user can browse the
		// catalogue before going hunting.
		const vEmptyHero = vCapturedCount === 0
			? renderEmptyState({
				variant: 'remarquables',
				title: 'Aucun remarquable capturé',
				body: 'Les arbres remarquables sont éparpillés dans tout Paris — parcs, places, allées historiques. Ouvre la carte, repère un ⭐ orange, approche-toi à moins de 30 m.',
				ctaLabel: '↗ Aller sur la carte',
				ctaHref: kPaths.map(),
			})
			: ''
		vList.innerHTML = vEmptyHero + vRows.map(renderCard).join('')
		vList.querySelectorAll<HTMLElement>('.arboretum-card').forEach((inCard) => {
			inCard.addEventListener('click', () => {
				const vId = Number(inCard.dataset.arbreId)
				if (Number.isFinite(vId)) navigate(kPaths.remarquable(vId))
			})
		})
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		vSubheader.innerHTML = `<span class="arboretum-error">⚠ ${escapeHtml(vMsg)}</span>`
		vList.innerHTML = ''
	}
}

function sortKey(inRow: CardRow): string
{
	const vQualif = inRow.info.qualif ?? 'zzz'  // missing-qualif rows last
	const vSpecies = inRow.speciesName?.commonName ?? formatLatin(inRow.speciesName)
	return `${vQualif}__${vSpecies}`
}

function renderCard(inRow: CardRow): string
{
	const vTitle = inRow.speciesName?.commonName ?? formatLatin(inRow.speciesName)
	const vSubtitle = inRow.speciesName?.commonName ? formatLatin(inRow.speciesName) : null
	const vSnippet = firstSentence(inRow.info.resume)
	const vGlyph = inRow.captured ? '⭐' : '✩'  // filled vs. outlined per discovery state
	const vQualif = inRow.info.qualif && inRow.info.qualif.length > 0 ? inRow.info.qualif : null

	return `
		<button class="arboretum-card rmq-card" role="listitem" data-arbre-id="${inRow.arbreId}" data-captured="${inRow.captured ? 'true' : 'false'}">
			<div class="arboretum-card-glyph rmq-card-glyph" aria-hidden="true">${vGlyph}</div>
			<div class="arboretum-card-body">
				<div class="arboretum-card-title">${escapeHtml(vTitle)}</div>
				${vSubtitle ? `<div class="arboretum-card-subtitle"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
				<div class="arboretum-card-meta">
					${vQualif ? `<span class="rmq-qualif-pill">${escapeHtml(vQualif)}</span>` : ''}
				</div>
				${vSnippet ? `<div class="arboretum-card-foot">${escapeHtml(vSnippet)}</div>` : ''}
			</div>
		</button>
	`
}

/*
First sentence of a free-text résumé, capped at ~120 chars. Strips any
duplicate sentences that the upstream OpenData export sometimes glues
together (the data pipeline collapses these too at build time, but we
handle the leftover defensively).
*/
function firstSentence(inText: string | undefined): string | null
{
	if (!inText) return null
	const vCleaned = inText.replace(/[\r\n]+/g, ' ').trim()  // flatten newlines
	const vEnd = vCleaned.search(/[.!?]\s/)  // first sentence terminator followed by space
	const vFirst = vEnd >= 0 ? vCleaned.slice(0, vEnd + 1) : vCleaned
	if (vFirst.length <= 120) return vFirst
	return `${vFirst.slice(0, 117)}…`
}

function formatLatin(inName: SpeciesName | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
